import { createSignal, createEffect, onCleanup, Show, For, type Component } from "solid-js";
import "./context-menu.css";

interface ContextEvent {
  clientX: number;
  clientY: number;
}

export interface ContextMenuItem {
  label?: string;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  separator?: boolean;
}

export interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

/**
 * ContextMenu — menu clic droit positionné et réutilisable.
 *
 * Features :
 * - Positionnement fixed à (x, y), repositionné si débordement écran
 * - Clic ailleurs, Escape, scroll → fermeture
 * - Items : separator, danger, disabled
 * - Navigation a11y : flèches Haut/Bas, Entrée
 *
 * Usage :
 * ```tsx
 * <ContextMenu
 *   x={mouseX()}
 *   y={mouseY()}
 *   items={[
 *     { label: "Edit", onSelect: handleEdit },
 *     { separator: true },
 *     { label: "Delete", danger: true, onSelect: handleDelete },
 *   ]}
 *   onClose={close}
 * />
 * ```
 */
export const ContextMenu: Component<ContextMenuProps> = (props) => {
  const [focusIndex, setFocusIndex] = createSignal(-1);
  let menuRef: HTMLDivElement | undefined;

  // Calculer la position avec reposition si débordement
  const getPosition = () => {
    if (!menuRef) return { x: props.x, y: props.y };

    const rect = menuRef.getBoundingClientRect();
    const x = props.x + rect.width > window.innerWidth ? Math.max(0, window.innerWidth - rect.width - 8) : props.x;
    const y = props.y + rect.height > window.innerHeight ? Math.max(0, window.innerHeight - rect.height - 8) : props.y;

    return { x, y };
  };

  const position = () => getPosition();

  // Fermer au clic/Escape/scroll
  createEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef && !menuRef.contains(e.target as Node)) {
        props.onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const nonSepItems = props.items.filter((item) => !item.separator);
      if (e.key === "Escape") {
        e.preventDefault();
        props.onClose();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const idx = focusIndex();
        setFocusIndex(idx <= 0 ? nonSepItems.length - 1 : idx - 1);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        const idx = focusIndex();
        setFocusIndex(idx >= nonSepItems.length - 1 ? 0 : idx + 1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const idx = focusIndex();
        if (idx >= 0 && idx < nonSepItems.length) {
          const item = nonSepItems[idx];
          if (!item.disabled && item.onSelect) {
            item.onSelect();
            props.onClose();
          }
        }
      }
    };

    const handleScroll = () => {
      props.onClose();
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("scroll", handleScroll, true);

    onCleanup(() => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("scroll", handleScroll, true);
    });
  });

  return (
    <div
      ref={menuRef}
      class="context-menu"
      role="menu"
      style={{
        left: `${position().x}px`,
        top: `${position().y}px`,
      }}
    >
      <For each={props.items}>
        {(item) => (
          <Show when={!item.separator} fallback={<div class="context-menu__sep" role="separator" />}>
            <button
              class={`context-menu__item ${item.danger ? "context-menu__item--danger" : ""} ${item.disabled ? "context-menu__item--disabled" : ""}`}
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                if (item.onSelect) {
                  item.onSelect();
                  props.onClose();
                }
              }}
              onMouseEnter={() => setFocusIndex(props.items.filter((i) => !i.separator).indexOf(item))}
              onMouseLeave={() => setFocusIndex(-1)}
            >
              {item.label}
            </button>
          </Show>
        )}
      </For>
    </div>
  );
};

/**
 * Hook helper — gère l'état du context menu et la logique d'ouverture.
 *
 * Usage :
 * ```tsx
 * const { menu, openAt, close } = createContextMenu();
 *
 * <table onContextMenu={(e) => openAt(e)}>
 *   ...
 * </table>
 *
 * <Show when={menu.show()}>
 *   <ContextMenu
 *     x={menu.x()}
 *     y={menu.y()}
 *     items={items}
 *     onClose={close}
 *   />
 * </Show>
 * ```
 */
export interface ContextMenuState {
  show: () => boolean;
  x: () => number;
  y: () => number;
  close: () => void;
}

export function createContextMenu() {
  const [show, setShow] = createSignal(false);
  const [x, setX] = createSignal(0);
  const [y, setY] = createSignal(0);

  const openAt = (e: MouseEvent | PointerEvent | ContextEvent) => {
    if ("preventDefault" in e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    setX(e.clientX);
    setY(e.clientY);
    setShow(true);
  };

  const close = () => setShow(false);

  const menu: ContextMenuState = {
    show,
    x,
    y,
    close,
  };

  return { menu, openAt, close };
}
